import React from "react";
import * as Icons from "lucide-react";
import { ORG } from "@/lib/constants";
import { PageHeader, SectionCard, InfoNote } from "@/components/uikit";
import { SystemInfoPanel } from "@/components/SystemInfoPanel";

export default function About() {
  return (
    <div>
      <PageHeader overline="About" title="About SignalLink AI" icon="Info"
        subtitle="An independent evidence layer for digital and AI-generated events." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SectionCard title="Mission" icon="Target" testId="about-mission">
            <p className="text-sm leading-relaxed text-slate-300">
              SignalLink AI provides a serious, defense-grade console for capturing events and producing independent,
              locally verifiable cryptographic evidence. The workflow is simple and auditable: {ORG.principle}.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Evidence is produced entirely inside your browser using native cryptography. Nothing leaves the device
              unless you export it. The console distinguishes clearly between local cryptographic evidence, simulated
              demo telemetry, and any externally verified evidence.
            </p>
          </SectionCard>

          <SectionCard title="What This System Does — and Does Not — Claim" icon="Scale" testId="about-claims">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="mb-2 flex items-center gap-2 font-heading text-xs font-bold uppercase text-emerald-300">
                  <Icons.CheckCircle2 className="h-4 w-4" /> Provides
                </p>
                <ul className="space-y-1.5 text-xs text-slate-300">
                  {["Local SHA-256 cryptographic evidence", "Deterministic canonicalization + chaining", "Tamper detection via hash recomputation", "Deterministic, explainable anomaly scoring", "State capture, replay & reconstruction", "Exportable JSON evidence receipts"].map((t) => (
                    <li key={t} className="flex items-start gap-2"><Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />{t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-2 flex items-center gap-2 font-heading text-xs font-bold uppercase text-rose-300">
                  <Icons.XCircle className="h-4 w-4" /> Never Claims
                </p>
                <ul className="space-y-1.5 text-xs text-slate-300">
                  {["Government approval or certification", "NIST certification (mapping only)", "Blockchain / RFC 3161 verification", "OpenAI or vendor endorsement", "Military deployment or customer validation", "Third-party notarization or signatures"].map((t) => (
                    <li key={t} className="flex items-start gap-2"><Icons.X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Accessibility — ADA Tier 1-A Interface Mode" icon="Accessibility" testId="about-accessibility">
            <p className="text-sm leading-relaxed text-slate-300">
              Toggle <strong>ADA Tier 1-A</strong> in the top bar to increase text size and spacing, enlarge touch
              targets, raise contrast, and suppress motion. The console supports keyboard navigation, semantic HTML,
              visible focus indicators, screen-reader labels, and never conveys critical information by colour alone.
            </p>
            <InfoNote tone="info" className="mt-3">
              “ADA Tier 1-A” is SignalLink's internal interface designation. It is <strong>not</strong> an official
              government accessibility certification.
            </InfoNote>
          </SectionCard>
        </div>

        <SystemInfoPanel testId="about-sysinfo" />
      </div>

      <div className="mt-8 rounded-lg border border-slate-800 bg-[#0D121D] p-6 text-center">
        <Icons.Fingerprint className="mx-auto mb-3 h-6 w-6 text-cyan-500" aria-hidden="true" />
        <p className="font-heading text-base italic tracking-wide text-slate-400" data-testid="about-tagline">“{ORG.tagline}”</p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-600">{ORG.name} · CAGE {ORG.cage}</p>
      </div>
    </div>
  );
}
